import { supabase } from '../lib/supabase';
import { auditService } from './auditService';

// ============================================================
// PARTNER SERVICE
// ============================================================
export const partnerService = {
  async getAll() {
    const { data, error } = await supabase?.from('partners')?.select('*')?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getById(id) {
    const { data, error } = await supabase?.from('partners')?.select(`
        *,
        partner_contacts(*),
        partner_documents(*),
        partner_certifications(*),
        partner_trainings(*)
      `)?.eq('id', id)?.single();
    if (error) throw error;
    return data;
  },

  async create(partnerData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('partners')?.insert({ ...partnerData, created_by: user?.id })?.select()?.single();
    if (error) throw error;
    auditService?.log({ action: 'CREATE', entityType: 'partner', entityId: data?.id, entityName: data?.legal_name, newValues: partnerData });
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('partners')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    auditService?.log({ action: 'UPDATE', entityType: 'partner', entityId: id, entityName: data?.legal_name, newValues: updates });
    return data;
  },

  async updateStatus(id, status) {
    return this.update(id, { status });
  }
};

// ============================================================
// PARTNER CONTACTS SERVICE
// ============================================================
export const contactsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('partner_contacts')?.select('*')?.eq('partner_id', partnerId)?.order('is_primary', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async create(contact) {
    const { data, error } = await supabase?.from('partner_contacts')?.insert(contact)?.select()?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('partner_contacts')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('partner_contacts')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// PARTNER DOCUMENTS SERVICE
// ============================================================
export const documentsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('partner_documents')?.select('*')?.eq('partner_id', partnerId)?.order('uploaded_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async upload(partnerId, docType, file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const filePath = `${partnerId}/${docType}/${Date.now()}_${file?.name}`;

    const { error: uploadError } = await supabase?.storage?.from('partner-documents')?.upload(filePath, file, { upsert: true });
    if (uploadError) throw uploadError;

    const { data, error } = await supabase?.from('partner_documents')?.insert({
        partner_id: partnerId,
        doc_type: docType,
        file_name: file?.name,
        file_path: filePath,
        file_size: file?.size,
        uploaded_by: user?.id
      })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async getSignedUrl(filePath) {
    const { data, error } = await supabase?.storage?.from('partner-documents')?.createSignedUrl(filePath, 3600);
    if (error) throw error;
    return data?.signedUrl;
  },

  async remove(id, filePath) {
    await supabase?.storage?.from('partner-documents')?.remove([filePath]);
    const { error } = await supabase?.from('partner_documents')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// OPPORTUNITIES SERVICE
// ============================================================
export const opportunitiesService = {
  async getAll(filters = {}) {
    let query = supabase?.from('opportunities')?.select('*, partners(legal_name, brand_name, tier)')?.order('created_at', { ascending: false });

    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('opp_status', filters?.status);
    }
    if (filters?.sector && filters?.sector !== 'all') {
      query = query?.eq('sector', filters?.sector);
    }
    if (filters?.role && filters?.role !== 'all') {
      query = query?.ilike('role', `%${filters?.role}%`);
    }
    if (filters?.search) {
      query = query?.ilike('name', `%${filters?.search}%`);
    }
    if (filters?.minValue) {
      query = query?.gte('value', parseFloat(filters?.minValue));
    }
    if (filters?.maxValue) {
      query = query?.lte('value', parseFloat(filters?.maxValue));
    }
    if (filters?.fromDate) {
      query = query?.gte('expected_close', filters?.fromDate);
    }
    if (filters?.toDate) {
      query = query?.lte('expected_close', filters?.toDate);
    }
    if (filters?.partner_id) {
      query = query?.eq('partner_id', filters?.partner_id);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(oppData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('opportunities')?.insert({ ...oppData, created_by: user?.id })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('opportunities')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// PROJECTS SERVICE
// ============================================================
export const projectsService = {
  async getAll(partnerId) {
    let query = supabase?.from('projects')?.select('*, partners(legal_name, brand_name)')?.order('created_at', { ascending: false });
    if (partnerId) query = query?.eq('partner_id', partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(projectData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('projects')?.insert({ ...projectData, created_by: user?.id })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('projects')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// TENDERS SERVICE
// ============================================================
export const tendersService = {
  async getAll(partnerId) {
    let query = supabase?.from('tenders')?.select('*, partners(legal_name, brand_name)')?.order('created_at', { ascending: false });
    if (partnerId) query = query?.eq('partner_id', partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('tenders')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// RISKS SERVICE
// ============================================================
export const risksService = {
  async getAll(partnerId) {
    let query = supabase?.from('risks')?.select('*')?.order('created_at', { ascending: false });
    if (partnerId) query = query?.eq('partner_id', partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(riskData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('risks')?.insert({ ...riskData, created_by: user?.id })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('risks')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// CAPA ACTIONS SERVICE
// ============================================================
export const capaService = {
  async getAll(partnerId) {
    let query = supabase?.from('capa_actions')?.select('*')?.order('created_at', { ascending: false });
    if (partnerId) query = query?.eq('partner_id', partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(capaData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('capa_actions')?.insert({ ...capaData, created_by: user?.id })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('capa_actions')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// PERFORMANCE METRICS SERVICE
// ============================================================
export const metricsService = {
  async getAll() {
    const { data, error } = await supabase?.from('performance_metrics')?.select('*')?.order('metric_month', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('performance_metrics')?.select('*')?.eq('partner_id', partnerId)?.order('metric_month', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async getLatest(partnerId) {
    const { data, error } = await supabase?.from('performance_metrics')?.select('*')?.eq('partner_id', partnerId)?.order('metric_month', { ascending: false })?.limit(1)?.maybeSingle();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// CERTIFICATIONS SERVICE
// ============================================================
export const certificationsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('partner_certifications')?.select('*')?.eq('partner_id', partnerId)?.order('expiry_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async create(certData) {
    const { data, error } = await supabase?.from('partner_certifications')?.insert(certData)?.select()?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('partner_certifications')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// TRAININGS SERVICE
// ============================================================
export const trainingsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase?.from('partner_trainings')?.select('*')?.eq('partner_id', partnerId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async update(id, updates) {
    const { data, error } = await supabase?.from('partner_trainings')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// LIFECYCLE CONTROL SERVICE
// ============================================================
export const lifecycleService = {
  async getRequests(filters = {}) {
    let query = supabase
      ?.from('lifecycle_requests')
      ?.select(`
        *,
        partners(legal_name, brand_name, tier, status),
        requester:user_profiles!lifecycle_requests_requested_by_fkey(full_name, email),
        reviewer:user_profiles!lifecycle_requests_reviewed_by_fkey(full_name, email)
      `)
      ?.order('created_at', { ascending: false });

    if (filters?.eventType && filters?.eventType !== 'all') {
      query = query?.eq('event_type', filters?.eventType);
    }
    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('request_status', filters?.status);
    }
    if (filters?.partnerId) {
      query = query?.eq('partner_id', filters?.partnerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async createRequest(requestData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('lifecycle_requests')
      ?.insert({ ...requestData, requested_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async updateRequest(id, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const payload = { ...updates };
    if (updates?.request_status === 'approved' || updates?.request_status === 'rejected') {
      payload.reviewed_by = user?.id;
      payload.reviewed_at = new Date()?.toISOString();
    }
    const { data, error } = await supabase
      ?.from('lifecycle_requests')
      ?.update(payload)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async getAuditLog(partnerId) {
    let query = supabase
      ?.from('lifecycle_audit_log')
      ?.select(`
        *,
        partners(legal_name, brand_name),
        performer:user_profiles!lifecycle_audit_log_performed_by_fkey(full_name, email)
      `)
      ?.order('created_at', { ascending: false });

    if (partnerId) {
      query = query?.eq('partner_id', partnerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async logAuditEvent(auditData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('lifecycle_audit_log')
      ?.insert({ ...auditData, performed_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};
